import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Popconfirm,
  message,
  Card,
  Typography,
  Tooltip,
  Tag,
  ColorPicker,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PagesWrapper from '../../../PagesWrapper';
import levelsOfDifficultyService, { isValidHexColor, normalizeHexColor } from '../../../services/levelsOfDifficultyService';
import styles from './LevelsOfDifficulty.module.css';

const { Title, Text } = Typography;

/**
 * Levels of Difficulty Management Page
 * Admin page for CRUD operations on difficulty level metadata
 */
const LevelsOfDifficulty = () => {
  const { t } = useTranslation();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [form] = Form.useForm();

  /**
   * Fetches levels from the API
   */
  const fetchLevels = useCallback(async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const response = await levelsOfDifficultyService.getLevels({
        page,
        pageSize,
        search: search || undefined,
      });
      setLevels(response.items || []);
      setPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      message.error(t('admin.levels.fetchError', 'Failed to load difficulty levels'));
      console.error('Failed to fetch levels:', error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchLevels(pagination.current, pagination.pageSize, searchText);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles table pagination changes
   */
  const handleTableChange = (newPagination) => {
    fetchLevels(newPagination.current, newPagination.pageSize, searchText);
  };

  /**
   * Handles search input
   */
  const handleSearch = (value) => {
    setSearchText(value);
    fetchLevels(1, pagination.pageSize, value);
  };

  /**
   * Opens the modal for creating/editing a level
   */
  const openModal = (level = null) => {
    setEditingLevel(level);
    if (level) {
      form.setFieldsValue({
        name: level.name,
        hexColor: level.hexColor,
        sortOrder: level.sortOrder,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ sortOrder: 0 });
    }
    setModalVisible(true);
  };

  /**
   * Closes the modal
   */
  const closeModal = () => {
    setModalVisible(false);
    setEditingLevel(null);
    form.resetFields();
  };

  /**
   * Handles form submission for create/update
   */
  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        hexColor: normalizeHexColor(values.hexColor),
      };
      
      if (editingLevel) {
        await levelsOfDifficultyService.updateLevel(editingLevel.id, data);
        message.success(t('admin.levels.updateSuccess', 'Difficulty level updated successfully'));
      } else {
        await levelsOfDifficultyService.createLevel(data);
        message.success(t('admin.levels.createSuccess', 'Difficulty level created successfully'));
      }
      closeModal();
      fetchLevels(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage || t('admin.levels.saveError', 'Failed to save difficulty level'));
    }
  };

  /**
   * Handles level deletion
   */
  const handleDelete = async (id) => {
    try {
      await levelsOfDifficultyService.deleteLevel(id);
      message.success(t('admin.levels.deleteSuccess', 'Difficulty level deleted successfully'));
      fetchLevels(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(t('admin.levels.deleteError', 'Failed to delete difficulty level'));
    }
  };

  /**
   * Handles color picker change
   */
  const handleColorChange = (color) => {
    const hexString = typeof color === 'string' ? color : color.toHexString();
    form.setFieldsValue({ hexColor: hexString });
  };

  /**
   * Table columns configuration
   */
  const columns = [
    {
      title: t('admin.levels.columns.color', 'Color'),
      dataIndex: 'hexColor',
      key: 'color',
      width: 100,
      align: 'center',
      render: (hexColor) => (
        <div
          className={styles.colorPreview}
          style={{ backgroundColor: hexColor }}
        />
      ),
    },
    {
      title: t('admin.levels.columns.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Tag color={record.hexColor} style={{ color: '#2c2c2c'}}>
          {name}
        </Tag>
      ),
    },
    {
      title: t('admin.levels.columns.hexColor', 'Hex Color'),
      dataIndex: 'hexColor',
      key: 'hexColor',
      width: 120,
      render: (hexColor) => (
        <code className={styles.hexCode}>{hexColor}</code>
      ),
    },
    {
      title: t('admin.levels.columns.sortOrder', 'Sort Order'),
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 120,
      align: 'center',
      sorter: (a, b) => a.sortOrder - b.sortOrder,
    },
    {
      title: t('admin.levels.columns.actions', 'Actions'),
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('common.edit', 'Edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('admin.levels.deleteConfirm', 'Are you sure you want to delete this difficulty level?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes', 'Yes')}
            cancelText={t('common.no', 'No')}
          >
            <Tooltip title={t('common.delete', 'Delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleSection}>
              <ThunderboltOutlined className={styles.titleIcon} />
              <Title level={2} className={styles.title}>
                {t('admin.levels.title', 'Levels of Difficulty')}
              </Title>
            </div>
            <Text type="secondary">
              {t('admin.levels.description', 'Manage difficulty level metadata for content categorization')}
            </Text>
          </div>

          <div className={styles.toolbar}>
            <Space>
              <Input.Search
                placeholder={t('admin.levels.searchPlaceholder', 'Search levels...')}
                allowClear
                onSearch={handleSearch}
                style={{ width: 300 }}
                prefix={<SearchOutlined />}
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchLevels(pagination.current, pagination.pageSize, searchText)}
              >
                {t('common.refresh', 'Refresh')}
              </Button>
            </Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openModal()}
            >
              {t('admin.levels.addLevel', 'Add Level')}
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={levels}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total, range) =>
                t('common.pagination', '{{start}}-{{end}} of {{total}} items', {
                  start: range[0],
                  end: range[1],
                  total,
                }),
            }}
            onChange={handleTableChange}
          />

        <Modal
          title={editingLevel
            ? t('admin.levels.editTitle', 'Edit Difficulty Level')
            : t('admin.levels.addTitle', 'Add Difficulty Level')
          }
          open={modalVisible}
          onCancel={closeModal}
          footer={null}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ sortOrder: 0 }}
          >
            <Form.Item
              name="name"
              label={t('admin.levels.form.name', 'Level Name')}
              rules={[
                { required: true, message: t('admin.levels.form.nameRequired', 'Please enter level name') },
                { max: 100, message: t('admin.levels.form.nameMaxLength', 'Name must be at most 100 characters') },
              ]}
            >
              <Input placeholder={t('admin.levels.form.namePlaceholder', 'e.g., Beginner')} />
            </Form.Item>

            <Form.Item
              name="hexColor"
              label={t('admin.levels.form.hexColor', 'Color')}
              rules={[
                { required: true, message: t('admin.levels.form.hexColorRequired', 'Please select a color') },
                {
                  validator: (_, value) => {
                    if (!value || isValidHexColor(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('admin.levels.form.hexColorInvalid', 'Invalid hex color format')));
                  },
                },
              ]}
            >
              <div className={styles.colorInputWrapper}>
                <Input
                  placeholder={t('admin.levels.form.hexColorPlaceholder', 'e.g., #4CAF50')}
                  addonBefore={
                    <ColorPicker
                      value={form.getFieldValue('hexColor')}
                      onChange={handleColorChange}
                      size="small"
                    />
                  }
                />
              </div>
            </Form.Item>

            <Form.Item
              name="sortOrder"
              label={t('admin.levels.form.sortOrder', 'Sort Order')}
              rules={[
                { required: true, message: t('admin.levels.form.sortOrderRequired', 'Please enter sort order') },
              ]}
              tooltip={t('admin.levels.form.sortOrderTooltip', 'Lower numbers appear first')}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item className={styles.formActions}>
              <Space>
                <Button onClick={closeModal}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingLevel
                    ? t('common.save', 'Save')
                    : t('common.create', 'Create')
                  }
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default LevelsOfDifficulty;
